export type SegmentId = 'lost' | 'stressed' | 'parent';

export interface Question {
  text: string;
  options: [string, string, string, string];
}

export interface SegmentContent {
  id: SegmentId;
  title: string;
  paper: string;
  paperDeep: string;
  lineColor: string;
  footerNote: string;
  figure: string;
  hero: {
    eyebrow: string;
    headlineA: string;
    headlineB: string;
    sub: string;
    primaryCta: string;
  };
  recognition: {
    kicker: string;
    heading: string;
    bullets: [string, string, string, string];
    closer: string;
  };
  mechanism: {
    kicker: string;
    bigNormal1: string;
    bigItalic1: string;
    bigNormal2: string;
    bigItalic2: string;
    p1: string;
    p2: string;
  };
  taster: {
    heading: string;
    questions: Question[];
  };
  result: {
    heading: string;
    body: string;
  };
}

export const SEGMENTS: Record<SegmentId, SegmentContent> = {
  lost: {
    id: 'lost',
    title: "Moodra — Find what's holding you back",
    paper: '#F4EEE4',
    paperDeep: '#EBE2D2',
    lineColor: '#D8CDB8',
    footerNote: "A belief diagnostic for when you can't name what's in the way.",
    figure: '/images/figure-lost.svg',
    hero: {
      eyebrow: "For when you can't name what's wrong",
      headlineA: 'You used to feel like you were going somewhere.',
      headlineB: 'When did that stop?',
      sub: "You're not depressed. You're not lazy. You're just\u00a0— stuck. And you can't explain why. Moodra helps you find what's actually holding you back.",
      primaryCta: "Find what's holding you back",
    },
    recognition: {
      kicker: 'If any of this sounds familiar',
      heading: "You're not the only one feeling it.",
      bullets: [
        'You\'re doing all the things you\'re "supposed" to do — and still feel flat.',
        "You watch other people move forward and can't tell what they have that you don't.",
        "You don't know if something is actually wrong, or if this is just what being an adult feels like.",
        'You\'ve tried to "just push through" — and the spark still hasn\'t come back.',
      ],
      closer: 'No advice here yet. Just — we see it.',
    },
    mechanism: {
      kicker: 'What Moodra actually does',
      bigNormal1: 'Most things tell you\u00a0',
      bigItalic1: 'what to do.',
      bigNormal2: ' Moodra helps you understand\u00a0',
      bigItalic2: "what's in the way.",
      p1: "In about 10 minutes, Moodra's assessment surfaces the specific beliefs keeping you stuck — not a generic personality type, the actual patterns underneath your stuckness.",
      p2: "Then it gives you a structured path to start shifting them. No streaks. No noise. Just clarity about the thing you couldn't name on your own.",
    },
    taster: {
      heading: 'No signup needed to start. Just answer honestly.',
      questions: [
        {
          text: 'When you imagine the next year of your life, what comes up first?',
          options: [
            'A quiet kind of dread',
            "Mostly a blank — I can't picture it",
            'The same as now, just more tired',
            'I keep waiting to feel ready',
          ],
        },
        {
          text: "When something good happens to you, what's your honest first reaction?",
          options: [
            'I wait for it to be taken away',
            'I move straight to the next thing',
            'I assume I got lucky',
            'I can enjoy it for a moment, then it fades',
          ],
        },
        {
          text: 'Finish this sentence: "I\'d move forward if only I\u2026"',
          options: [
            '\u2026knew what I actually wanted',
            '\u2026believed I could pull it off',
            "\u2026wasn't so afraid of getting it wrong",
            '\u2026had permission from someone',
          ],
        },
        {
          text: 'Where did you first learn that your worth had to be earned?',
          options: [
            'At home, growing up',
            'At school or early work',
            "I've never thought about it like that",
            'It feels like I was always this way',
          ],
        },
        {
          text: 'If the stuckness had a voice, what would it keep telling you?',
          options: [
            '"Not yet. You\'re not ready."',
            '"Who do you think you are?"',
            '"It\'s safer to stay here."',
            '"This is just how it is for you."',
          ],
        },
      ],
    },
    result: {
      heading: "That's the surface. Your full pattern report goes much deeper.",
      body: 'Your personalized report names the specific beliefs keeping you stuck — and the structured path to shift them.',
    },
  },

  stressed: {
    id: 'stressed',
    title: 'Moodra — Find the belief driving your stress',
    paper: '#ECEEF3',
    paperDeep: '#DEE2EC',
    lineColor: '#C5CBDA',
    footerNote: 'A belief diagnostic — not a stress relief app.',
    figure: '/images/figure-stressed.svg',
    hero: {
      eyebrow: 'For high achievers in demanding careers',
      headlineA: "You're managing.",
      headlineB: 'Barely.',
      sub: "No breathing app is going to fix why. The stress isn't the problem\u00a0— it's the symptom. Moodra finds the belief underneath it.",
      primaryCta: 'Find the belief driving your stress',
    },
    recognition: {
      kicker: "If you're high-performing and quietly exhausted",
      heading: "You're not the only one running on fumes.",
      bullets: [
        'Sunday evenings have started to feel heavier than they used to.',
        "You've stopped really switching off — even on holidays.",
        'The "busy season" keeps not ending.',
        "You're achieving more and feeling worse — and you can't say that out loud anywhere.",
      ],
      closer: 'Not here to soothe it. Here to name it.',
    },
    mechanism: {
      kicker: 'A belief diagnostic — not a meditation app',
      bigNormal1: 'Meditation apps manage\u00a0',
      bigItalic1: 'the symptom.',
      bigNormal2: ' Moodra finds\u00a0',
      bigItalic2: 'the cause.',
      p1: "In about 10 minutes, Moodra's diagnostic identifies the belief pattern driving your stress — perfectionism, worth-through-output, control-as-safety. The specific one running you.",
      p2: "Then it gives you a structured path to shift it at the root. Not relaxation. Not a streak. Root-cause work for the part of you that can't slow down.",
    },
    taster: {
      heading: 'No signup needed. Answer the way you actually are, not the way you perform.',
      questions: [
        {
          text: "When you finally have an empty evening, what's the first thing you feel?",
          options: [
            'A little guilty, like I should be doing more',
            'A flicker of relief, then the to-do list creeps in',
            "Restless — I'm not sure what to do with it",
            "Honestly, I can barely remember the last one",
          ],
        },
        {
          text: 'What\'s the cost of saying "no" at work, for you?',
          options: [
            "I'll be seen as not committed",
            "Someone else has to pick it up — I can't do that to them",
            "I won't be invited to the next thing",
            "I don't really know how to say no",
          ],
        },
        {
          text: 'When something goes well at work, what do you tell yourself?',
          options: [
            'It could still fall apart',
            'I should have done it sooner / better',
            'The bar is higher now',
            'I barely register it before the next thing',
          ],
        },
        {
          text: "If you're honest — what does your job give you that you'd struggle to get elsewhere?",
          options: [
            "Proof that I'm capable",
            "Identity — I don't fully know who I am without it",
            'A sense of control over my life',
            "Permission to be exhausted (it's a good reason)",
          ],
        },
        {
          text: "If the part of you that can't slow down had a sentence, what would it be?",
          options: [
            '"If I stop, it all falls apart."',
            '"I\'m only as good as my last delivery."',
            '"Resting means I didn\'t earn it."',
            '"I\'ll rest when this is over." (It is never over.)',
          ],
        },
      ],
    },
    result: {
      heading: "That's the surface. The belief underneath goes deeper.",
      body: 'Your personalized report names the specific belief driving your stress — and the structured path to change it at the root.',
    },
  },

  parent: {
    id: 'parent',
    title: "Moodra — Find what you're carrying",
    paper: '#EFEBFA',
    paperDeep: '#E4DDF5',
    lineColor: '#C9BFE2',
    footerNote: 'A belief diagnostic for parents who want the cycle to stop with them.',
    figure: '/images/figure-parent.svg',
    hero: {
      eyebrow: "For parents who swore they'd do it differently",
      headlineA: "You swore you wouldn't parent like them.",
      headlineB: 'Then you heard yourself.',
      sub: "The moment you sound exactly like your parent — it's not failure. It's the signal. Moodra helps you find what you're carrying.",
      primaryCta: "Find what you're carrying",
    },
    recognition: {
      kicker: "If you've had this moment",
      heading: 'Almost every honest parent knows it.',
      bullets: [
        'The tone came out of you — and you recognised it instantly. It was theirs.',
        'You reacted the way you swore you never would, and felt it land on your child.',
        'You know the patterns you grew up in. You can feel them moving through you anyway.',
        "You don't want to be perfect. You just don't want to pass this particular thing on.",
      ],
      closer: 'No judgement here. Just — we recognise the moment.',
    },
    mechanism: {
      kicker: 'What Moodra actually does for parents',
      bigNormal1: 'Most parenting tools focus on\u00a0',
      bigItalic1: 'your child.',
      bigNormal2: ' Moodra focuses on\u00a0',
      bigItalic2: "what's running inside you.",
      p1: "In about 10 minutes, Moodra's assessment surfaces the inherited patterns shaping how you parent — not advice about your child, insight about what you're carrying.",
      p2: "Then it gives you a structured path to interrupt the cycle — so the pattern stops with you, not with them.",
    },
    taster: {
      heading: 'No signup needed. There are no wrong answers and no one is grading you as a parent.',
      questions: [
        {
          text: 'When was the last time you reacted to your child in a way that surprised you afterwards?',
          options: [
            'This week',
            'This month',
            'A while ago, but I still think about it',
            "I'd rather not think about it",
          ],
        },
        {
          text: 'In that moment, whose voice did you hear coming out of you?',
          options: [
            "My mother's",
            "My father's",
            'Someone else who raised me',
            "It sounded like me — and that's almost worse",
          ],
        },
        {
          text: "What's one thing you swore you'd never do as a parent — and have caught yourself doing?",
          options: [
            "Yelling when I'm overwhelmed",
            'Going quiet / cold when I\'m hurt',
            "Saying something that lands harder than I meant it to",
            "Pushing them when they're already trying",
          ],
        },
        {
          text: "When your child is upset, what's your honest first instinct?",
          options: [
            'Fix it as fast as possible',
            "Match their intensity — I can't help it",
            'Go a little numb until it passes',
            'Make sure no one outside sees it',
          ],
        },
        {
          text: "If the pattern you're afraid of passing on had a sentence, what would it be?",
          options: [
            '"Love is something you have to earn."',
            '"Don\'t make a fuss."',
            '"I\'m only okay when you\'re okay."',
            '"Don\'t show them the soft parts."',
          ],
        },
      ],
    },
    result: {
      heading: "That's the surface. What you're carrying goes deeper.",
      body: 'Your personalized report names the specific patterns you inherited — and where to start so they stop with you.',
    },
  },
};

export const DEFAULT_SEGMENT: SegmentId = 'lost';
